<script setup lang="ts">
    import { onMounted, ref, watch } from 'vue';
    import { longDate, explainCrontabSchedule } from '@/formatters';
    import BoolElement from '@/shared/components/BoolElement.vue';
    import Spinner from '@/shared/components/Spinner.vue';
    import { requireIsAdministrator as vRequireIsAdministrator } from '@/shared/directives/requirePermission';
    import {
        addSchedule,
        adminPageScheduleOutput,
        deleteSchedule,
        deleteScheduleExecution,
        getScheduleExecutions,
        getSchedules,
        runSchedule,
        Schedule,
        ScheduleExecution,
        ScheduleUpdateModel,
        updateSchedule
    } from '@/shared/helpers/api';
    import { http } from '@/shared/httpWrapper';
    import useShow from '@/shared/composables/useShow';
    import useBuefy from '@/shared/composables/useBuefy';
    import moment from 'moment';

    const { showInfo, showError } = useShow();
    const buefy = useBuefy();

    // --- State ---

    const schedules = ref<Schedule[]>([]);
    const executions = ref<ScheduleExecution[]>([]);
    const scheduleListOpen = ref<boolean>(true);
    const loading = ref<boolean>(true);
    const saving = ref<boolean>(false);
    const editSchedule = ref<Schedule | null>(null);

    // --- Watcher ---

    watch(editSchedule, async (value) => {
        if (value && value.id > 0) {
            await loadExecutions(value.id);
        } else {
            executions.value = [];
        }
    });

    // --- Lifecycle ---

    onMounted(load);

    // --- Functions ---

    async function load(): Promise<void> {
        const data = await getSchedules(http);
        schedules.value = data.sort((a, b) => a.description.localeCompare(b.description));
        loading.value = false;
    }

    async function loadExecutions(id: number): Promise<void> {
        executions.value = [];
        executions.value = await getScheduleExecutions(http, id);
    }

    async function ensureOutput(row: ScheduleExecution): Promise<void> {
        if (!row.output) {
            const tmp = await adminPageScheduleOutput(http, row.id);
            row.output = tmp.output;
        }
    }

    function clearSelected(): void {
        editSchedule.value = null;
        executions.value = [];
    }

    function clickNewSchedule(): void {
        editSchedule.value = {
            action: '',
            scheduleSpec: '0 0 * * *',
            enabled: true,
            description: '',
            category: '',
            icons: '',
            configuration: '',
            id: -1,
            lastKnownState: 'Ok',
            lastExecutionTime: ''
        };
    }

    async function clickSaveSchedule(): Promise<void> {
        const current = editSchedule.value;
        if (!current || saving.value) return;

        saving.value = true;
        try {
            if (current.id !== -1) {
                const result = await updateSchedule(http, { ...current });
                const index = schedules.value.findIndex((s) => s.id === current.id);
                if (index !== -1) {
                    schedules.value[index] = result.data;
                }
            } else {
                await addSchedule(http, current as ScheduleUpdateModel);
                await load();
                editSchedule.value = null;
            }
            showInfo('Schedule saved');
        } catch (e) {
            showError(e);
        } finally {
            saving.value = false;
        }
    }

    async function clickRunNow(): Promise<void> {
        if (!editSchedule.value) return;
        await runSchedule(http, editSchedule.value as ScheduleUpdateModel);
    }

    function confirmDialog(message: string, okText: string): Promise<void> {
        return new Promise((resolve, reject) => {
            buefy.dialog.confirm({
                message,
                confirmText: okText,
                cancelText: 'Cancel',
                onConfirm: () => resolve(),
                onCancel: () => reject()
            });
        });
    }

    async function clickDeleteSchedule(id: number): Promise<void> {
        await confirmDialog('This will permanently delete schedule', 'Confirm delete')
            .then(async () => {
                try {
                    await deleteSchedule(http, id);
                    schedules.value = schedules.value.filter((s) => s.id !== id);
                    clearSelected();
                } catch (e) {
                    showError(e);
                }
            }).catch(() => { });
    }

    async function clickDeleteExecution(id: number): Promise<void> {
        await confirmDialog('This will permanently delete schedule execution from log', 'Confirm delete')
            .then(async () => {
                try {
                    await deleteScheduleExecution(http, id);
                    executions.value = executions.value.filter((s) => s.id !== id);
                } catch (e) {
                    showError(e);
                }
            }).catch(() => { });
    }

    function executionDuration(execution: ScheduleExecution): string {
        const start = execution.startDate;
        const end = execution.endDate;
        if (start && end) {
            return moment.duration(moment(start).diff(end)).humanize();
        }
        return '';
    }
</script>

<template>
    <div>
        <div class="level is-mobile">
            <div class="level-left">
                <h2 class="title is-3">
                    Schedules
                </h2>
            </div>
            <div class="level-right">
                <b-button
                    v-require-is-administrator
                    type="is-info"
                    @click="clickNewSchedule">
                    <b-icon
                        icon="plus-circle"
                        size="is-small"
                        aria-hidden="true" />
                    <span>New schedule</span>
                </b-button>
            </div>
        </div>
        <b-collapse
            v-model="scheduleListOpen"
            aria-id="attributeCollapse">
            <template #trigger>
                <h2
                    class="title is-4"
                    aria-controls="attributeCollapse">
                    All tasks
                    <b-icon
                        :icon="scheduleListOpen ? 'chevron-up' : 'chevron-down'"
                        class="align-icon-left" />
                </h2>
            </template>
            &nbsp;
            <spinner :loading="loading">
                <b-table
                    v-model:selected="editSchedule"
                    :data="schedules"
                    default-sort="category"
                    :striped="true"
                    :narrowed="true"
                    :hoverable="true"
                    height="40vh"
                    sticky-header>
                    <b-table-column
                        v-slot="props"
                        field="icons"
                        label="Icons"
                        sortable>
                        {{ props.row.icons }}
                    </b-table-column>
                    <b-table-column
                        v-slot="props"
                        field="category"
                        label="Category"
                        sortable
                        searchable>
                        {{ props.row.category }}
                    </b-table-column>
                    <b-table-column
                        v-slot="props"
                        field="description"
                        label="Description"
                        sortable
                        searchable>
                        {{ props.row.description }}
                    </b-table-column>
                    <b-table-column
                        v-slot="props"
                        label="Schedule (UTC based)">
                        {{ explainCrontabSchedule(props.row.scheduleSpec) }}
                    </b-table-column>
                    <b-table-column
                        v-slot="props"
                        field="lastExecutionTime"
                        label="Last run (UTC)"
                        sortable>
                        {{ longDate(props.row.lastExecutionTime) }}
                    </b-table-column>
                    <b-table-column
                        v-slot="props"
                        field="lastKnownState"
                        label="State"
                        sortable>
                        <b-icon
                            v-if="props.row.lastKnownState === 'Failed'"
                            icon="close-circle"
                            size="is-small"
                            pack="mdi"
                            style="color:red;" />
                        <b-icon
                            v-else-if="props.row.lastKnownState === 'Running'"
                            size="is-small"
                            pack="mdi"
                            icon="loading"
                            custom-class="mdi-spin"
                            style="color:blue;" />
                        <b-icon
                            v-else
                            size="is-small"
                            pack="mdi"
                            icon="check-circle"
                            style="color:green;" />
                    </b-table-column>
                    <b-table-column
                        v-slot="props"
                        field="enabled"
                        label="Enabled"
                        sortable>
                        <bool-element :value="props.row.enabled" />
                    </b-table-column>
                </b-table>
            </spinner>
        </b-collapse>
        <div
            v-if="editSchedule"
            style="margin-top:20px;margin-bottom: 10px">
            <b-field grouped>
                <b-field
                    label="Icons"
                    label-position="on-border"
                    expanded>
                    <b-input
                        v-model="editSchedule.icons"
                        type="text"
                        placeholder="icons" />
                </b-field>
                <b-field
                    label="Category"
                    label-position="on-border"
                    expanded>
                    <b-input
                        v-model="editSchedule.category"
                        type="text"
                        placeholder="category" />
                </b-field>
            </b-field>
            <b-field grouped>
                <b-field
                    label="Description"
                    label-position="on-border"
                    expanded>
                    <b-input
                        v-model="editSchedule.description"
                        type="text"
                        placeholder="description" />
                </b-field>
                <b-field
                    label="Action"
                    label-position="on-border"
                    expanded>
                    <b-input
                        v-model="editSchedule.action"
                        type="text"
                        placeholder="action"
                        required />
                </b-field>
            </b-field>
            <b-field
                label="Configuration"
                label-position="on-border">
                <b-input
                    v-model="editSchedule.configuration"
                    class="configuration"
                    type="textarea" />
            </b-field>
            <b-field grouped>
                <b-field
                    label="Schedule"
                    label-position="on-border"
                    expanded>
                    <b-input
                        v-model="editSchedule.scheduleSpec"
                        type="text"
                        placeholder="* * * * *" />
                    <p class="control">
                        <span class="button is-static">
                            {{ explainCrontabSchedule(editSchedule.scheduleSpec) }}
                        </span>
                    </p>
                </b-field>
                <b-field>
                    <b-switch
                        v-model="editSchedule.enabled"
                        v-require-is-administrator>
                        <strong>{{ editSchedule.enabled ? 'Enabled' : 'Disabled' }}</strong>
                    </b-switch>
                </b-field>
            </b-field>
            <hr>
            <div class="buttons">
                <b-button
                    v-require-is-administrator
                    type="is-primary"
                    outlined
                    :loading="saving"
                    @click="clickSaveSchedule">
                    <b-icon
                        icon="floppy"
                        pack="mdi"
                        size="is-small"
                        aria-hidden="true" />
                    <span>Save</span>
                </b-button>
                <b-button
                    v-show="editSchedule.id > 0"
                    v-require-is-administrator
                    type="is-danger"
                    outlined
                    @click="clickDeleteSchedule(editSchedule.id)">
                    <b-icon
                        icon="delete-outline"
                        pack="mdi"
                        size="is-small"
                        aria-hidden="true" />
                    <span>Delete</span>
                </b-button>
                <b-button
                    v-show="editSchedule.id > 0"
                    v-require-is-administrator
                    type="is-info"
                    outlined
                    @click="clickRunNow()">
                    <b-icon
                        icon="play"
                        pack="mdi"
                        size="is-small"
                        aria-hidden="true" />
                    <span>Run NOW!</span>
                </b-button>
                <b-button
                    v-show="editSchedule.id > 0"
                    type="is-info"
                    outlined
                    @click="loadExecutions(editSchedule.id)">
                    <b-icon
                        icon="reload"
                        pack="mdi"
                        size="is-small"
                        aria-hidden="true" />
                    <span>Reload log</span>
                </b-button>
            </div>
        </div>
        <b-table
            :data="executions"
            default-sort="startDate"
            default-sort-direction="desc"
            :show-detail-icon="true"
            detailed
            :striped="true"
            :narrowed="true"
            :hoverable="true"
            class="site-sticky-header"
            @details-open="(row: ScheduleExecution) => ensureOutput(row)">
            <b-table-column
                v-slot="props"
                label="State">
                <b-icon
                    v-if="props.row.failed"
                    size="is-small"
                    pack="mdi"
                    icon="close-circle"
                    style="color:red;" />
                <b-icon
                    v-else-if="props.row.running"
                    size="is-small"
                    icon="loading"
                    pack="mdi"
                    custom-class="mdi-spin"
                    style="color:blue;" />
                <b-icon
                    v-else
                    size="is-small"
                    pack="mdi"
                    icon="check-circle"
                    style="color:green;" />
            </b-table-column>
            <b-table-column
                v-slot="props"
                field="startDate"
                label="Start"
                sortable>
                {{ longDate(props.row.startDate) }}
            </b-table-column>
            <b-table-column
                v-slot="props"
                field="endDate"
                label="End"
                sortable>
                {{ longDate(props.row.endDate) }}
            </b-table-column>
            <b-table-column
                v-slot="props"
                label="Duration">
                {{ executionDuration(props.row) }}
            </b-table-column>
            <b-table-column
                v-slot="props"
                field="id"
                style="justify-content:flex-end"
                label="Delete"
                numeric>
                <a
                    class="button"
                    @click="clickDeleteExecution(props.row.id)"><b-icon
                        icon="delete"
                        size="is-small" /></a>
            </b-table-column>
            <template #detail="props">
                <pre>{{ props.row.output }}</pre>
            </template>
        </b-table>
    </div>
</template>

<style scoped>
.configuration textarea {
    font-family: 'Courier New', Courier, monospace;
}
.detail-container pre {
    padding: 0px;
    background-color: black;
    color: lightgreen;
}
</style>
